import { z } from 'zod';

import { EmailSchema, PhoneNumberSchema } from '@vestigas/utils';

import { certificateRoleEnum } from '../../common';

import { COMPANY_ACCOUNT_FEATURES } from './companyAccountFeatures';
import {
  DEFAULT_STRATEGIC_MULTIPLIER,
  zodStrategicMultiplierSchema,
} from './strategicMultiplier';

// Define features schema dynamically based on the features config. Also
// reused as the read-side schema for `FeatureFlagsCompanyAccount` (`GET
// /company_account/{id}`, see `useQueryCompanyAccount/validations.ts`): every
// key defaults rather than going through `.optional()`, which both matches
// the backend's own per-flag defaults and keeps the output free of
// `undefined` — a bare `.optional()` here would make the response schema's
// output fail the ky client's `ZodType<Json>` bound (see `KyValidationOptions.response`
// in `packages/data/src/kyClient.ts`).
export const zodFeaturesSchema = Object.fromEntries(
  Object.entries(COMPANY_ACCOUNT_FEATURES).map(([key, value]) => [
    key,
    // We only have 2 types for now so using a simple ternary
    value.type === 'number'
      ? z.coerce.number().default(value.default)
      : z.coerce.boolean(),
  ]),
);

export const zodCompanyAccountFormCreateSchema = z.object({
  data: z.object({
    defaultSignatureRole: z
      .union([z.literal(''), certificateRoleEnum])
      .optional(),
    featureFlags: z.object(zodFeaturesSchema),
    supportEmail: z.union([EmailSchema, z.literal('')]).nullish(),
    supportPhoneNumber: z.union([PhoneNumberSchema, z.literal('')]).nullish(),
  }),
  name: z.string().min(1),
  strategicMultiplier: zodStrategicMultiplierSchema.prefault(
    DEFAULT_STRATEGIC_MULTIPLIER,
  ),
  syncWithStaging: z.boolean().optional().prefault(true),
});

export type CompanyAccountFormCreateType = z.infer<
  typeof zodCompanyAccountFormCreateSchema
>;

export const zodCompanyAccountFormUpdateSchema =
  zodCompanyAccountFormCreateSchema
    .omit({ syncWithStaging: true })
    .extend({ baseOuId: z.uuid() });

export type CompanyAccountFormUpdateType = z.infer<
  typeof zodCompanyAccountFormUpdateSchema
>;
